// 紀錄 — sales history with the full breakdown (lines, discounts, payments,
// change, points) and 退款 / 退貨 / 換貨. Online orders (WooCommerce /
// Shopify connectors, later) show with a 🌐 tag next to 🏪 counter sales.
import { zhEn, dateLocale } from "./posTier.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Printer, Undo2, ArrowLeftRight, RefreshCw, ScanLine, Trash2 } from "lucide-react";
import { C, Btn, Chip, Pill, Seg, Card, Sheet, Stepper, Label, Toggle, Empty, money, round2, inputStyle, useWide } from "./posUi.jsx";
import { refundLineValue, posErrorText, newId, effectivePrice } from "./posMath.js";
import { printReceipt, receiptFromSale, methodLabel, payLabel } from "./posReceipt.js";
import { ProductThumb } from "./Register.jsx";
import { fetchSales, refundSale } from "../../lib/api.js";

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const RANGES = {
  today: () => [dayStart(new Date()), null],
  yesterday: () => { const s = dayStart(new Date()); s.setDate(s.getDate() - 1); return [s, dayStart(new Date())]; },
  d7: () => { const s = dayStart(new Date()); s.setDate(s.getDate() - 6); return [s, null]; },
  month: () => { const s = dayStart(new Date()); s.setDate(1); return [s, null]; },
  d90: () => { const s = dayStart(new Date()); s.setDate(s.getDate() - 89); return [s, null]; },
};
export const rangeIso = (key) => { const [a, b] = (RANGES[key] || RANGES.today)(); return { from: a.toISOString(), to: b ? b.toISOString() : null }; };

const SOURCE = { pos: ["🏪", zhEn("門市", "In store")], woocommerce: ["🌐", zhEn("網店", "Online")], shopify: ["🌐", "Shopify"], import: ["📦", zhEn("匯入", "Imported")] };
const timeOf = (iso) => new Date(iso).toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
const dateOf = (iso) => new Date(iso).toLocaleDateString(dateLocale(), { month: "numeric", day: "numeric" });

export default function History({ can = () => true, has = () => true, tenant, sym, customerById, products, methods, onChanged, onScanOpen, refreshKey }) {
  const wide = useWide(900);
  const [range, setRange] = useState("today");
  const [channel, setChannel] = useState("all");
  const [q, setQ] = useState("");
  const [sales, setSales] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [refund, setRefund] = useState(null);   // {sale, mode}

  const load = useCallback(async () => {
    setErr("");
    try { setSales(await fetchSales(tenant.id, rangeIso(range))); }
    catch (e) { setErr(posErrorText(e)); setSales([]); }
  }, [tenant.id, range]);
  useEffect(() => { setSales(null); load(); }, [load, refreshKey]);

  const hasWeb = !!tenant?.wcEnabled || (sales || []).some(s => s.source !== "pos");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^#/, "");
    return (sales || []).filter(x => (channel === "all" || (channel === "pos" ? x.source === "pos" : x.source !== "pos")) &&
      (!s || String(x.number) === s || (customerById(x.customer_id)?.name || "").toLowerCase().includes(s) ||
        (x.sale_lines || []).some(l => (l.name || "").toLowerCase().includes(s))));
  }, [sales, channel, q, customerById]);
  const totalNet = list.reduce((s, x) => s + Number(x.total), 0);
  const byId = useMemo(() => Object.fromEntries((sales || []).map(s => [s.id, s])), [sales]);
  const open = openId ? byId[openId] : null;

  return (
    <div style={{ display: wide ? "flex" : "block", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="pos-noscroll" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }}>
          {[["today", zhEn("今日", "Today")], ["yesterday", zhEn("尋日", "Yesterday")], ["d7", zhEn("7 日", "7 days")], ["month", zhEn("本月", "This month")], ["d90", zhEn("90 日", "90 days")]].map(([k, l]) => <Chip key={k} on={range === k} onClick={() => setRange(k)}>{l}</Chip>)}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("單號、客戶、商品", "Receipt no., customer, product")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
          </div>
          <button type="button" onClick={load} aria-label={zhEn("重新整理", "Refresh")} style={{ width: 46, borderRadius: 14, border: "1.5px solid " + C.line, background: "#fff", color: C.ink, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><RefreshCw size={18} /></button>
        </div>
        {hasWeb && <Seg value={channel} onChange={setChannel} style={{ marginBottom: 10 }} options={[["all", zhEn("全部", "All")], ["pos", zhEn("🏪 門市", "🏪 In store")], ["web", zhEn("🌐 網店", "🌐 Online")]]} />}

        {sales && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 900, color: C.sub, margin: "0 4px 8px" }}>
            <span>{list.length} {zhEn("張單", "sales")}</span><span>{zhEn("淨額", "Net")} {money(totalNet, sym)}</span>
          </div>
        )}
        {err && <p style={{ color: C.red, fontWeight: 800 }}>{err}</p>}
        {sales === null ? <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 30 }}>{zhEn("載入中…", "Loading…")}</p>
          : list.length === 0 ? <Empty icon="🧾" title={zhEn("呢段時間冇單", "No sales in this period")} />
          : list.map(s => {
            const cust = customerById(s.customer_id);
            const lines = (s.sale_lines || []);
            const pays = (s.sale_payments || []);
            const src = SOURCE[s.source] || SOURCE.pos;
            return (
              <Card key={s.id} onClick={() => setOpenId(s.id)} style={{ padding: "12px 14px", marginBottom: 8, border: "1px solid " + (openId === s.id && wide ? C.accent : C.line) }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 900, color: C.ink }}>#{s.number}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.sub }}>{range === "today" ? timeOf(s.created_at) : dateOf(s.created_at) + " " + timeOf(s.created_at)}</span>
                  {hasWeb && <Pill tone={s.source === "pos" ? "grey" : "blue"}>{src[0]} {src[1]}</Pill>}
                  {s.kind === "refund" && <Pill tone="red">{zhEn("退款", "Refund")}</Pill>}
                  {s.kind === "exchange" && <Pill tone="blue">{zhEn("換貨", "Exchange")}</Pill>}
                  {s.status === "partial_refund" && <Pill tone="amber">{zhEn("部分退款", "Part refund")}</Pill>}
                  {s.status === "refunded" && <Pill tone="grey">{zhEn("已退款", "Refunded")}</Pill>}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 16, fontWeight: 900, color: Number(s.total) < 0 ? C.red : C.ink }}>{money(s.total, sym)}</span>
                </div>
                <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cust?.name ? cust.name + " · " : ""}{lines.map(l => l.name + (Math.abs(Number(l.qty)) !== 1 ? " ×" + Math.abs(Number(l.qty)) : "")).join("、")}
                </p>
                <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                  {pays.map(p => <Pill key={p.id} tone="pink">{payLabel(methodLabel(p))} {money(p.amount, sym)}</Pill>)}
                </div>
              </Card>
            );
          })}
      </div>

      {wide ? (
        open && <div style={{ width: 420, flexShrink: 0, position: "sticky", top: 12 }}>
          <Card style={{ padding: 16, maxHeight: "calc(100vh - 140px)", overflowY: "auto" }}>
            <SaleDetail sale={open} sym={sym} tenant={tenant} customerById={customerById} byId={byId} canRefund={can("refund") && has("standard")} refundLocked={!has("standard")} onRefund={(mode) => setRefund({ sale: open, mode })} onOpen={setOpenId} />
          </Card>
        </div>
      ) : (
        <Sheet open={!!open} onClose={() => setOpenId(null)} title={open ? (open.kind === "refund" ? zhEn("退款單", "Refund") : open.kind === "exchange" ? zhEn("換貨單", "Exchange") : zhEn("單", "Sale")) + " #" + open.number : ""}>
          {open && <SaleDetail sale={open} sym={sym} tenant={tenant} customerById={customerById} byId={byId} canRefund={can("refund") && has("standard")} refundLocked={!has("standard")} onRefund={(mode) => setRefund({ sale: open, mode })} onOpen={setOpenId} />}
        </Sheet>
      )}

      {refund && (
        <RefundSheet sale={refund.sale} initialMode={refund.mode} tenant={tenant} sym={sym} products={products} methods={methods}
          customer={customerById(refund.sale.customer_id)} onScanOpen={onScanOpen}
          onClose={() => setRefund(null)}
          onDone={async (res) => { setRefund(null); setOpenId(res?.refundId || null); await load(); onChanged?.(res); }} />
      )}
    </div>
  );
}

export function SaleDetail({ sale, sym, tenant, customerById, byId, onRefund, onOpen, canRefund = true, refundLocked = false }) {
  const cust = customerById(sale.customer_id);
  const lines = sale.sale_lines || [];
  const pays = sale.sale_payments || [];
  const orig = sale.original_sale_id ? byId[sale.original_sale_id] : null;
  const refundable = sale.kind !== "refund" && sale.status !== "refunded" && sale.status !== "voided" && Number(sale.total) > 0 &&
    lines.some(l => l.kind !== "wallet_topup" && Number(l.qty) - Number(l.refunded_qty || 0) > 0);
  const hasProducts = lines.some(l => l.kind === "product" && Number(l.qty) - Number(l.refunded_qty || 0) > 0);
  return (
    <div>
      <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 800, color: C.sub }}>{new Date(sale.created_at).toLocaleString(dateLocale())}</p>
      <p style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 900, color: C.ink }}>{cust?.name || zhEn("散客", "Walk-in")}{cust?.phone ? " · " + cust.phone : ""}</p>
      {sale.original_sale_id && (
        <button type="button" onClick={() => onOpen(sale.original_sale_id)} style={{ border: "none", background: C.blueSoft, color: "#1D4ED8", borderRadius: 12, padding: "8px 12px", fontWeight: 900, fontSize: 13, fontFamily: "inherit", cursor: "pointer", marginBottom: 12 }}>
          {zhEn("↩︎ 原單 #", "↩︎ Original #")}{orig?.number || "…"}
        </button>
      )}

      <Label>{zhEn("項目", "Items")}</Label>
      <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: "2px 12px", marginBottom: 12 }}>
        {lines.map((l, i) => {
          const refunded = Number(l.refunded_qty || 0);
          const listP = l.original_price == null ? null : Number(l.original_price);
          return (
            <div key={l.id} style={{ padding: "10px 0", borderTop: i ? "1px solid " + C.line : "none" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 900, color: C.ink }}>{l.kind === "appointment" ? "✂️ " : l.kind === "wallet_topup" ? "💳 " : ""}{l.name}</span>
                <span style={{ fontSize: 14, fontWeight: 900, color: Number(l.total) < 0 ? C.red : C.ink }}>{money(l.total, sym)}</span>
              </div>
              <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 700, color: C.sub }}>
                {Math.abs(Number(l.qty))} × {money(l.unit_price, sym)}
                {listP != null && listP !== Number(l.unit_price) && <span> {zhEn("· 原價", "· list price")} {money(listP, sym)}</span>}
                {Number(l.discount) > 0 && <span style={{ color: C.red }}> · {l.meta?.promoName ? zhEn("促銷「", "Promo \"") + l.meta.promoName + zhEn("」", "\"") : zhEn("折 ", "Off ") + (l.meta?.discountPct ? l.meta.discountPct + "% " : "")}−{money(l.discount, sym)}</span>}
                {l.meta?.override && <span style={{ color: C.amber }}> {zhEn("· 手動改價", "· manual price")}</span>}
                {l.kind === "wallet_topup" && Number(l.meta?.bonus) > 0 && <span> {zhEn("· 送", "· bonus")} {money(l.meta.bonus, sym)}</span>}
                {refunded > 0 && <span style={{ color: C.red }}> {zhEn("· 已退", "· refunded")} {refunded}</span>}
                {l.meta?.restock === false && <span> {zhEn("· 冇入返倉", "· not restocked")}</span>}
              </p>
              {l.meta?.note && <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 700, color: C.sub }}>📝 {l.meta.note}</p>}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 12, padding: "0 4px" }}>
        {Number(sale.order_discount) > 0 && <><DRow l={zhEn("小計", "Subtotal")} r={money(sale.subtotal, sym)} /><DRow l={zhEn("整單折扣", "Order discount")} r={"−" + money(sale.order_discount, sym)} color={C.red} /></>}
        <DRow l={zhEn("合計", "Total")} r={money(sale.total, sym)} big />
      </div>

      <Label>{zhEn("付款", "Payment")}</Label>
      <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: "2px 12px", marginBottom: 12 }}>
        {pays.map((p, i) => (
          <div key={p.id} style={{ padding: "10px 0", borderTop: i ? "1px solid " + C.line : "none" }}>
            <DRow l={payLabel(methodLabel(p)) + (p.points ? " · " + Math.abs(p.points) + zhEn(" 分", " pts") : "")} r={money(p.amount, sym)} />
            {p.tendered != null && <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("收", "Received")} {money(p.tendered, sym)} {zhEn("· 找", "· change")} {money(p.change_given, sym)}</p>}
            {Number(p.apt_amount) !== 0 && Number(p.apt_amount) !== Number(p.amount) && <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("其中", "of which")} {money(p.apt_amount, sym)} {zhEn("付美容預約", "for the booking")}</p>}
          </div>
        ))}
      </div>
      {Number(sale.change_due) > 0 && <DRow l={zhEn("找續", "Change")} r={money(sale.change_due, sym)} />}
      {Number(sale.points_earned) !== 0 && <p style={{ fontSize: 13, fontWeight: 800, color: Number(sale.points_earned) > 0 ? C.green : C.red, margin: "6px 4px" }}>⭐ {Number(sale.points_earned) > 0 ? zhEn("賺取 ", "Earned ") : zhEn("扣回 ", "Deducted ")}{Math.abs(sale.points_earned)} {zhEn("分", "pts")}</p>}
      {sale.note && <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, margin: "6px 4px" }}>📝 {sale.note}</p>}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <Btn kind="ghost" small onClick={() => printReceipt(receiptFromSale(sale, cust?.name), { tenant, sym })}><Printer size={16} /> {zhEn("收據", "Receipt")}</Btn>
        {refundable && canRefund && <Btn kind="danger" small onClick={() => onRefund("refund")}><Undo2 size={16} /> {zhEn("退款 / 退貨", "Refund / return")}</Btn>}
        {refundable && canRefund && hasProducts && <Btn kind="soft" small onClick={() => onRefund("exchange")}><ArrowLeftRight size={16} /> {zhEn("換貨", "Exchange")}</Btn>}
        {refundable && !canRefund && <span style={{ fontSize: 12, fontWeight: 800, color: C.sub, alignSelf: "center" }}>{refundLocked ? zhEn("🔒 退款 / 換貨要專業版", "🔒 Refunds & exchanges need the Pro plan") : zhEn("退款需要店主操作", "Refunds need the owner")}</span>}
      </div>
    </div>
  );
}
const DRow = ({ l, r, color, big }) => <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color, fontSize: big ? 17 : undefined, fontWeight: big ? 900 : undefined }}><span>{l}</span><span>{r}</span></div>;

// 退款 / 換貨. Values follow refund_sale exactly (order discount shared pro-rata).
function RefundSheet({ sale, initialMode, tenant, sym, products, methods, customer, onClose, onDone, onScanOpen }) {
  const [mode, setMode] = useState(initialMode || "refund");
  const lines = (sale.sale_lines || []).filter(l => l.kind !== "wallet_topup" && Number(l.qty) - Number(l.refunded_qty || 0) > 0);
  const [qty, setQty] = useState(() => Object.fromEntries(lines.map(l => [l.id, lines.length === 1 ? Number(l.qty) - Number(l.refunded_qty || 0) : 0])));
  const [restock, setRestock] = useState(() => Object.fromEntries(lines.map(l => [l.id, true])));
  const [method, setMethod] = useState(() => {
    const ps = (sale.sale_payments || []).filter(p => Number(p.amount) > 0);
    return ps.length === 1 ? ps[0].method : "cash";
  });
  const [payMethod, setPayMethod] = useState("cash");
  const [newItems, setNewItems] = useState([]);   // [{productId, name, qty, price}]
  const [pq, setPq] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const idRef = useState(() => ({ refund: newId(), exchange: newId() }))[0];

  const value = round2(lines.reduce((s, l) => s + refundLineValue(sale, l, qty[l.id] || 0), 0));
  const newTotal = round2(newItems.reduce((s, it) => s + it.qty * it.price, 0));
  const credit = mode === "exchange" ? Math.min(value, newTotal) : 0;
  const refundOut = round2(value - credit);           // money going back
  const payIn = mode === "exchange" ? round2(newTotal - credit) : 0;  // money coming in
  const redeemValue = Math.max(0.01, Number(tenant?.loyaltyRedeemValue) || 1);

  const refundOpts = [["cash", zhEn("現金", "Cash")]];
  if (sale.customer_id) refundOpts.push(["wallet", zhEn("退去錢包", "To wallet")]);
  if (sale.customer_id && (sale.sale_payments || []).some(p => p.method === "points")) refundOpts.push(["points", zhEn("退返積分", "Back to points")]);
  for (const m of methods || []) refundOpts.push([m.presetKey || m.name, m.name.replace(/^\p{Extended_Pictographic}\s*/u, "")]);
  const optName = (k) => (refundOpts.find(o => o[0] === k) || [k, k])[1];

  // Wallet / points refunds must be whole units — any remainder goes back as cash.
  const tenders = (amount, m) => {
    if (amount <= 0) return [];
    if (m === "wallet") {
      const w = Math.floor(amount), rest = round2(amount - w);
      return [...(w > 0 ? [{ method: "wallet", methodName: zhEn("錢包", "Wallet"), amount: w }] : []), ...(rest > 0 ? [{ method: "cash", methodName: zhEn("現金", "Cash"), amount: rest }] : [])];
    }
    if (m === "points") {
      const pts = Math.floor(amount / redeemValue), pa = round2(pts * redeemValue), rest = round2(amount - pa);
      return [...(pts > 0 ? [{ method: "points", methodName: zhEn("積分", "Points"), amount: pa, points: pts }] : []), ...(rest > 0 ? [{ method: "cash", methodName: zhEn("現金", "Cash"), amount: rest }] : [])];
    }
    return [{ method: m, methodName: optName(m), amount: round2(amount) }];
  };

  const shownProducts = useMemo(() => {
    const s = pq.trim().toLowerCase();
    if (!s) return [];
    return products.filter(p => p.status !== "draft" && p.sellable && (p.name.toLowerCase().includes(s) || p.barcode === s || p.sku.toLowerCase() === s)).slice(0, 8);
  }, [products, pq]);
  const addNew = (p) => setNewItems(items => {
    const i = items.findIndex(x => x.productId === p.id);
    if (i >= 0) return items.map((x, j) => j === i ? { ...x, qty: x.qty + 1 } : x);
    return [...items, { productId: p.id, name: p.name, qty: 1, price: effectivePrice(p), list: Number(p.price) }];
  });

  const canSubmit = value > 0 && !busy && (mode === "refund" || newItems.length > 0);
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const refundPayments = [
        ...(credit > 0 ? [{ method: "exchange_credit", methodName: "換貨抵扣", amount: credit }] : []),
        ...tenders(refundOut, method),
      ];
      const payload = {
        id: idRef.refund, tenantId: tenant.id, originalSaleId: sale.id, note,
        lines: lines.filter(l => qty[l.id] > 0).map(l => ({ saleLineId: l.id, qty: qty[l.id], restock: l.kind === "product" ? !!restock[l.id] : false })),
        payments: refundPayments,
      };
      if (mode === "exchange") {
        payload.exchange = {
          id: idRef.exchange, customerId: sale.customer_id || null, note: "換貨 #" + sale.number,
          lines: newItems.map(it => ({ kind: "product", productId: it.productId, qty: it.qty, unitPrice: it.price, originalPrice: it.list, discount: 0 })),
          payments: [
            ...(credit > 0 ? [{ method: "exchange_credit", methodName: "換貨抵扣", amount: credit }] : []),
            ...(payIn > 0 ? [{ method: payMethod, methodName: payMethod === "cash" ? zhEn("現金", "Cash") : optName(payMethod), amount: payIn }] : []),
          ],
        };
      }
      const res = await refundSale(payload);
      onDone(res);
    } catch (e) { setErr(posErrorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onClose={busy ? undefined : onClose} title={(mode === "exchange" ? zhEn("換貨", "Exchange") : zhEn("退款 / 退貨", "Refund / return")) + " · #" + sale.number} maxWidth={620}
      footer={
        <div>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "0 0 8px", textAlign: "center" }}>{err}</p>}
          <Btn kind={mode === "exchange" ? "primary" : "danger"} disabled={!canSubmit} onClick={submit} style={{ width: "100%", padding: 16, ...(mode === "refund" ? { background: C.red, color: "#fff" } : null) }}>
            {busy ? zhEn("處理中…", "Processing…") : mode === "refund"
              ? zhEn("退款 ", "Refund ") + money(value, sym)
              : payIn > 0 ? zhEn("換貨 · 客人補 ", "Exchange · customer pays ") + money(payIn, sym) : refundOut > 0 ? zhEn("換貨 · 退 ", "Exchange · refund ") + money(refundOut, sym) : zhEn("換貨 · 兩清", "Exchange · even")}
          </Btn>
        </div>
      }>
      <Seg value={mode} onChange={setMode} style={{ marginBottom: 14 }} options={[["refund", zhEn("退款 / 退貨", "Refund / return")], ...(lines.some(l => l.kind === "product") ? [["exchange", zhEn("換貨", "Exchange")]] : [])]} />

      <Label>{mode === "exchange" ? zhEn("退回邊啲", "Items coming back") : zhEn("退邊啲", "Items to refund")}</Label>
      {lines.map(l => {
        const left = Number(l.qty) - Number(l.refunded_qty || 0);
        const v = refundLineValue(sale, l, qty[l.id] || 0);
        return (
          <div key={l.id} style={{ background: "#fff", border: "1px solid " + (qty[l.id] ? C.accent : C.line), borderRadius: 16, padding: 12, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 14 }}>{l.kind === "appointment" ? "✂️ " : ""}{l.name}</p>
                <p style={{ margin: 0, fontWeight: 700, color: C.sub, fontSize: 12 }}>{zhEn("可退", "Can refund")} {left} {zhEn("· 每件", "· each")} {money(refundLineValue(sale, l, 1), sym)}{qty[l.id] ? zhEn(" · 退 ", " · refund ") + money(v, sym) : ""}</p>
              </div>
              {l.kind === "appointment"
                ? <Toggle on={!!qty[l.id]} onChange={on => setQty(q => ({ ...q, [l.id]: on ? 1 : 0 }))} />
                : <Stepper value={qty[l.id] || 0} min={0} max={left} onChange={v2 => setQty(q => ({ ...q, [l.id]: v2 }))} />}
            </div>
            {l.kind === "product" && qty[l.id] > 0 && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 13, fontWeight: 800, color: C.ink }}>
                <Toggle on={!!restock[l.id]} onChange={on => setRestock(r => ({ ...r, [l.id]: on }))} /> {zhEn("放返入倉（貨品完好）", "Return to stock (item in good condition)")}
              </label>
            )}
            {l.kind === "appointment" && qty[l.id] > 0 && <p style={{ margin: "8px 0 0", fontSize: 12, fontWeight: 800, color: C.amber }}>{zhEn("預約會變返「未付」", "The booking goes back to \"unpaid\"")}</p>}
          </div>
        );
      })}

      {mode === "exchange" && (
        <div style={{ marginTop: 14 }}>
          <Label>{zhEn("換成邊啲貨", "Exchange for")}</Label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input value={pq} onChange={e => setPq(e.target.value)} placeholder={zhEn("搜尋或者輸入條碼", "Search or enter a barcode")} style={{ ...inputStyle, flex: 1 }}
              onKeyDown={e => { if (e.key === "Enter") { const hit = products.find(p => p.barcode === pq.trim() || p.sku === pq.trim()); if (hit) { addNew(hit); setPq(""); } } }} />
            <button type="button" onClick={() => onScanOpen?.((code) => { const hit = products.find(p => p.barcode === code || p.sku === code); if (hit) addNew(hit); return !!hit; })} style={{ width: 46, borderRadius: 12, border: "none", background: C.grad, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ScanLine size={20} /></button>
          </div>
          {shownProducts.map(p => (
            <button key={p.id} type="button" onClick={() => { addNew(p); setPq(""); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: 8, border: "none", borderBottom: "1px solid " + C.line, background: "none", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
              <ProductThumb p={p} size={36} radius={10} /><span style={{ flex: 1, fontWeight: 800, color: C.ink }}>{p.name}</span><span style={{ fontWeight: 900 }}>{money(effectivePrice(p), sym)}</span>
            </button>
          ))}
          {newItems.map((it, i) => (
            <div key={it.productId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid " + C.line }}>
              <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14 }}>{it.name}<br /><span style={{ color: C.sub, fontSize: 12 }}>{money(it.price, sym)}</span></span>
              <Stepper value={it.qty} min={1} onChange={v => setNewItems(xs => xs.map((x, j) => j === i ? { ...x, qty: v } : x))} />
              <button type="button" onClick={() => setNewItems(xs => xs.filter((_, j) => j !== i))} style={{ border: "none", background: "none", color: C.faint, cursor: "pointer" }}><Trash2 size={18} /></button>
            </div>
          ))}
          <div style={{ background: C.accentSoft, borderRadius: 14, padding: "10px 12px", marginTop: 10, fontSize: 14, fontWeight: 900, color: C.ink }}>
            <DRow l={zhEn("退回貨品價值", "Value of returned items")} r={money(value, sym)} />
            <DRow l={zhEn("新貨", "New items")} r={money(newTotal, sym)} />
            <DRow l={payIn > 0 ? zhEn("客人要補", "Customer pays") : zhEn("要退畀客人", "Refund to customer")} r={money(payIn > 0 ? payIn : refundOut, sym)} color={payIn > 0 ? C.accent : C.red} />
          </div>
          {payIn > 0 && (
            <>
              <Label style={{ marginTop: 12 }}>{zhEn("補差價用", "Pay the difference with")}</Label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {refundOpts.filter(o => o[0] !== "wallet" && o[0] !== "points").map(([k, l]) => <Chip key={k} on={payMethod === k} onClick={() => setPayMethod(k)}>{l}</Chip>)}
              </div>
            </>
          )}
        </div>
      )}

      {(mode === "refund" || refundOut > 0) && value > 0 && (
        <>
          <Label style={{ marginTop: 14 }}>{zhEn("點樣退錢", "Refund to")}</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {refundOpts.map(([k, l]) => <Chip key={k} on={method === k} onClick={() => setMethod(k)}>{l}</Chip>)}
          </div>
          {(method === "wallet" || method === "points") && refundOut % 1 !== 0 && <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "6px 2px 0" }}>{zhEn("錢包 / 積分只退整數，餘下", "Wallet / points refund whole amounts only; the rest is")} {money(tenders(refundOut, method).find(t => t.method === "cash")?.amount || 0, sym)} {zhEn("用現金退。", "refunded in cash.")}</p>}
          {customer && <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "6px 2px 0" }}>{zhEn("原單賺嘅零售積分會按比例扣返。", "Retail points earned on the original sale are taken back proportionally.")}</p>}
        </>
      )}
      <div style={{ marginTop: 12 }}>
        <Label>{zhEn("原因（可留空）", "Reason (optional)")}</Label>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder={zhEn("例如 尺寸唔啱", "e.g. wrong size")} style={inputStyle} />
      </div>
    </Sheet>
  );
}
